import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { INCIDENT_TYPES, INCIDENT_CATEGORIES, INCIDENT_SEVERITIES } from "@/lib/incident-types";
import { getIncidentTemplate } from "@/lib/incident-templates";
import { captureEventLocation, NO_LOCATION } from "@/lib/location-capture";
import { ensureLocationConsent } from "@/lib/location-consent";
import { reverseGeocodeLocation } from "@/lib/location.functions";
import { enqueueFieldOp, putBlob } from "@/lib/offline-field-queue";
import { flushFieldQueue } from "@/lib/offline-field-sync";

/**
 * True only when the filing attempt failed for a reason a later retry could
 * fix (offline, dropped request, gateway/server hiccup). Permission denials
 * and data validation errors are permanent and must be surfaced immediately.
 */
function isRetryableFilingError(error: unknown): boolean {
  if (!error) return true; // no row returned, no explicit error — treat as transient
  const e = error as { code?: string; message?: string; status?: number };
  const code = e.code ?? "";
  const message = (e.message ?? "").toLowerCase();
  if (/failed to fetch|networkerror|network request failed|load failed|timeout|aborted/.test(message)) return true;
  if (code === "" && !e.status) return true;
  // PostgREST/Postgres SQLSTATEs: 42501 = RLS/permission, 23xxx = constraint,
  // 22xxx = data error, PGRST* = request rejected. None get better on retry.
  if (/^(42|23|22|21|2F|38|39|3F|40P|44)/.test(code) || code.startsWith("PGRST")) return false;
  if (typeof e.status === "number" && e.status >= 400 && e.status < 500) return false;
  return true;
}

export const Route = createFileRoute("/_authenticated/incidents/new")({
  component: NewIncidentPage,
  validateSearch: (s: Record<string, unknown>): { subtype?: string; category?: string } => ({
    subtype: typeof s.subtype === "string" ? s.subtype : undefined,
    category: typeof s.category === "string" ? s.category : undefined,
  }),
  head: () => ({ meta: [{ title: "New report — MissionReady" }] }),
});

function NewIncidentPage() {
  const { currentOrg, user } = useAuth();
  const navigate = useNavigate();
  const { subtype: prefilledSubtype, category: prefilledCategory } = Route.useSearch();
  const [sites, setSites] = useState<{ id: string; name: string }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Photos/videos chosen in the field. They are stored on the device with the
  // report so a report filed with no signal keeps its evidence.
  const [media, setMedia] = useState<File[]>([]);

  // Tracks which subtype's template is currently applied so we only overwrite
  // fields the user hasn't edited when they switch types.
  const [appliedSubtype, setAppliedSubtype] = useState<string>(prefilledSubtype ?? "");

  // Build initial form from deep-link params so users land on a pre-populated
  // template instead of an empty form.
  const buildInitialForm = (subtype?: string, category?: string) => {
    const tpl = getIncidentTemplate(subtype);
    return {
      category: category ?? "incident",
      subtype: subtype ?? "",
      severity: (tpl?.severity ?? "medium") as string,
      site_id: "",
      title: tpl?.title ?? "",
      narrative: tpl?.narrative ?? "",
      location_description: tpl?.location_description ?? "",
      people_involved: tpl?.people_involved ?? "",
      witness_names: tpl?.witness_names ?? "",
      occurrence_at: new Date().toISOString().slice(0, 16),
    };
  };

  const [form, setForm] = useState(() => buildInitialForm(prefilledSubtype, prefilledCategory));

  // Re-apply template when search params change (browser back/forward or
  // external navigation into an already-mounted page).
  useEffect(() => {
    setForm((prev) => {
      const tpl = getIncidentTemplate(prefilledSubtype);
      if (!tpl) return { ...prev, category: prefilledCategory ?? prev.category, subtype: prefilledSubtype ?? "" };
      return {
        ...prev,
        category: prefilledCategory ?? prev.category,
        subtype: prefilledSubtype ?? "",
        severity: (tpl.severity ?? prev.severity) as string,
        title: prev.title || tpl.title || "",
        narrative: prev.narrative || tpl.narrative || "",
        location_description: prev.location_description || tpl.location_description || "",
        people_involved: prev.people_involved || tpl.people_involved || "",
        witness_names: prev.witness_names || tpl.witness_names || "",
      };
    });
    if (prefilledSubtype) setAppliedSubtype(prefilledSubtype);
  }, [prefilledSubtype, prefilledCategory]);

  const applyTemplate = (nextSubtype: string) => {
    const prev = getIncidentTemplate(appliedSubtype);
    const next = getIncidentTemplate(nextSubtype);
    setForm((f) => {
      const keep = (current: string, prevVal?: string, nextVal?: string) =>
        !current.trim() || current === (prevVal ?? "") ? (nextVal ?? "") : current;
      return {
        ...f,
        subtype: nextSubtype,
        severity:
          next && f.severity === (prev?.severity ?? "medium")
            ? next.severity
            : f.severity,
        title: keep(f.title, prev?.title, next?.title),
        narrative: keep(f.narrative, prev?.narrative, next?.narrative),
        location_description: keep(f.location_description, prev?.location_description, next?.location_description),
        people_involved: keep(f.people_involved, prev?.people_involved, next?.people_involved),
        witness_names: keep(f.witness_names, prev?.witness_names, next?.witness_names),
      };
    });
    setAppliedSubtype(nextSubtype);
  };

  useEffect(() => {
    if (!currentOrg) return;
    supabase.from("sites").select("id,name").eq("organization_id", currentOrg.id).eq("active", true)
      .then(({ data }) => setSites((data as any) ?? []));
  }, [currentOrg?.id]);

  const isIncident = form.category === "incident";

  const submit = async () => {
    if (!currentOrg || !user) return;
    if (isIncident && !form.subtype) { toast.error("Choose an incident type"); return; }
    if (!form.narrative.trim()) { toast.error("Narrative is required"); return; }
    setSubmitting(true);
    // Location is captured at the moment of filing, only with consent, and a
    // failed reading never blocks the report.
    const consented = await ensureLocationConsent(currentOrg.id);
    const loc = consented ? await captureEventLocation() : NO_LOCATION;
    const occurredAt = new Date(form.occurrence_at).toISOString();
    const clientId = crypto.randomUUID();

    // Same report, saved on the device, when there is no connection or the
    // filing attempt fails. It syncs by itself and can never double-file
    // because the server keys on this report's client id.
    const queueReport = async () => {
      const blobs = [];
      for (let i = 0; i < media.length; i++) {
        blobs.push(await putBlob(clientId, i, media[i]!));
      }
      await enqueueFieldOp({
        id: clientId,
        kind: "incident",
        occurredAt,
        orgId: currentOrg.id,
        userId: user.id,
        siteId: form.site_id || null,
        blobs,
        payload: {
          category: form.category,
          subtype: isIncident ? form.subtype : null,
          severity: form.severity,
          title: form.title || null,
          narrative: form.narrative,
          locationDescription: form.location_description || null,
          peopleInvolved: form.people_involved || null,
          witnessNames: form.witness_names || null,
          latitude: loc.latitude,
          longitude: loc.longitude,
          accuracyM: loc.accuracyM,
          locationStatus: loc.status,
          locationCapturedAt: loc.capturedAt,
        },
      });
    };

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      await queueReport();
      setSubmitting(false);
      toast.success("Saved on your device — it will file itself when you're back online.");
      navigate({ to: "/incidents" });
      return;
    }

    let address: string | null = null;
    if (loc.latitude != null && loc.longitude != null) {
      try {
        const r = (await reverseGeocodeLocation({
          data: { latitude: loc.latitude, longitude: loc.longitude },
        })) as { address: string | null };
        address = r.address;
      } catch {
        address = null;
      }
    }
    const { data, error } = await supabase.from("incidents").insert({
      organization_id: currentOrg.id,
      reporter_id: user.id,
      site_id: form.site_id || null,
      client_id: clientId,
      category: form.category,
      subtype: isIncident ? form.subtype : null,
      severity: form.severity,
      title: form.title || null,
      narrative: form.narrative,
      location_description: form.location_description || null,
      people_involved: form.people_involved || null,
      witness_names: form.witness_names || null,
      occurrence_at: occurredAt,
      status: "open",
      latitude: loc.latitude,
      longitude: loc.longitude,
      accuracy_m: loc.accuracyM,
      location_status: loc.status,
      location_captured_at: loc.capturedAt,
      location_address: address,
    } as never).select("id,reference_number").single();
    if (error || !data) {
      // Only a connection problem should be queued. A refusal from the server
      // (no permission, invalid data) would fail again on every retry, so the
      // guard must see it now instead of a false "saved" message.
      if (!isRetryableFilingError(error)) {
        setSubmitting(false);
        toast.error(
          error?.message
            ? `Report not filed: ${error.message}`
            : "Report not filed. Please check the details and try again.",
        );
        return;
      }
      await queueReport();
      setSubmitting(false);
      toast.success("Saved on your device — it will file itself automatically.");
      void flushFieldQueue();
      navigate({ to: "/incidents" });
      return;
    }
    // Attachments upload after the report exists; a failure queues them
    // instead of losing the photo or video.
    if (media.length) {
      try {
        for (const file of media) {
          const safeName = file.name.replace(/[^\w.\-]+/g, "_");
          const path = `${currentOrg.id}/${data.id}/${Date.now()}-${safeName}`;
          const up = await supabase.storage.from("incident-attachments").upload(path, file, {
            contentType: file.type,
            upsert: true,
          });
          if (up.error) throw new Error(up.error.message);
          const { error: attErr } = await supabase.from("incident_attachments").insert({
            organization_id: currentOrg.id,
            incident_id: data.id,
            uploader_id: user.id,
            storage_path: path,
            file_name: file.name,
            mime_type: file.type || null,
            size_bytes: file.size,
          } as never);
          if (attErr) throw new Error(attErr.message);
        }
      } catch {
        toast.message("Report filed. Photos will finish uploading in the background.");
      }
    }
    setSubmitting(false);
    toast.success(`Filed ${data.reference_number}`);
    navigate({ to: "/incidents/$incidentId", params: { incidentId: data.id } });
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2"><Link to="/incidents"><ArrowLeft className="mr-1 h-4 w-4" /> All incidents</Link></Button>
      <h1 className="font-display text-2xl font-bold md:text-3xl">File a report</h1>
      <p className="text-sm text-muted-foreground">Reports are stamped with a reference number and added to the timeline.</p>

      <div className="mt-6 grid gap-4 rounded-xl border border-border bg-card p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger aria-label="Category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INCIDENT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger aria-label="Severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INCIDENT_SEVERITIES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isIncident && (
          <div className="grid gap-1.5">
            <Label>Incident type</Label>
            <Select value={form.subtype} onValueChange={applyTemplate}>
              <SelectTrigger aria-label="Incident type"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {INCIDENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Site</Label>
            <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
              <SelectTrigger aria-label="Site"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Occurrence date & time</Label>
            <Input aria-label="Occurrence date & time" type="datetime-local" value={form.occurrence_at} onChange={(e) => setForm({ ...form, occurrence_at: e.target.value })} />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label>Short title (optional)</Label>
          <Input aria-label="Short title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Brief summary" />
        </div>

        <div className="grid gap-1.5">
          <Label>Location details</Label>
          <Input aria-label="Location details" value={form.location_description} onChange={(e) => setForm({ ...form, location_description: e.target.value })} placeholder="e.g. North parking lot, level 2" />
        </div>

        <div className="grid gap-1.5">
          <Label>Narrative *</Label>
          <Textarea aria-label="Narrative *" rows={6} value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} placeholder="What happened, in chronological detail." />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>People involved</Label>
            <Textarea aria-label="People involved" rows={3} value={form.people_involved} onChange={(e) => setForm({ ...form, people_involved: e.target.value })} placeholder="Names, descriptions, roles" />
          </div>
          <div className="grid gap-1.5">
            <Label>Witnesses</Label>
            <Textarea aria-label="Witnesses" rows={3} value={form.witness_names} onChange={(e) => setForm({ ...form, witness_names: e.target.value })} placeholder="Names and contact info" />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label>Photos & video (optional)</Label>
          <Input aria-label="Photos & video (optional)"
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => setMedia(Array.from(e.target.files ?? []))}
          />
          {media.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {media.length} file{media.length > 1 ? "s" : ""} attached. They stay saved on this device until they upload.
            </p>
          )}
        </div>


        <div className="flex justify-end gap-2">
          <Button asChild variant="outline"><Link to="/incidents">Cancel</Link></Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "Filing…" : "File report"}</Button>
        </div>
      </div>
    </div>
  );
}
